import { useCallback, useState, useSyncExternalStore } from 'react'
import { exchangeDateOf } from '@/lib/exchange-time'

interface Clock {
  now: number
  listeners: Set<() => void>
  timer?: ReturnType<typeof setInterval>
}

// One shared clock per interval, so a list of subscribers costs one timer and they
// all advance together.
const clocks = new Map<number, Clock>()

function clockFor(intervalMs: number): Clock {
  let clock = clocks.get(intervalMs)
  if (!clock) {
    clock = { now: Date.now(), listeners: new Set() }
    clocks.set(intervalMs, clock)
  }
  return clock
}

function readClock(intervalMs: number): number {
  const clock = clockFor(intervalMs)
  // An idle clock doesn't tick. Catch it up at most once per interval, so repeated
  // snapshot reads in one render agree.
  const now = Date.now()
  if (
    clock.listeners.size === 0 &&
    Math.floor(now / intervalMs) !== Math.floor(clock.now / intervalMs)
  ) {
    clock.now = now
  }
  return clock.now
}

function subscribeClock(intervalMs: number, onTick: () => void): () => void {
  const clock = clockFor(intervalMs)
  clock.listeners.add(onTick)
  if (clock.listeners.size === 1) {
    clock.timer = setInterval(() => {
      clock.now = Date.now()
      clock.listeners.forEach((listener) => listener())
    }, intervalMs)
  }
  return () => {
    clock.listeners.delete(onTick)
    if (clock.listeners.size === 0) clearInterval(clock.timer)
  }
}

/**
 * A value derived from the current time, re-derived every `intervalMs`. `select` must
 * return a primitive, or the same instance for equal inputs: the component re-renders
 * only when the result changes.
 */
export function useClock<T>(intervalMs: number, select: (now: number) => T): T {
  // A running clock's last tick can predate this mount by up to an interval.
  const [mountedAt] = useState(Date.now)
  const subscribe = useCallback(
    (onTick: () => void) => subscribeClock(intervalMs, onTick),
    [intervalMs],
  )
  return useSyncExternalStore(subscribe, () => select(Math.max(mountedAt, readClock(intervalMs))))
}

const identity = (now: number) => now

/**
 * `Date.now()`, refreshed every `intervalMs`. Read "now" through this in render so
 * relative-time labels advance on their own instead of only when something else
 * re-renders the component.
 */
export function useNow(intervalMs: number): number {
  return useClock(intervalMs, identity)
}

let today: Date | null = null

function startOfLocalDay(now: number): Date {
  const day = new Date(now)
  day.setHours(0, 0, 0, 0)
  if (today?.getTime() !== day.getTime()) today = day
  return today
}

/**
 * Local midnight of the current day, the same `Date` all day. For "Today" labels: the
 * component re-renders when the day changes, not every minute.
 */
export function useToday(): Date {
  return useClock(60_000, startOfLocalDay)
}

/** The exchange's current date (`YYYY-MM-DD`); re-renders when it rolls over. */
export function useExchangeToday(exchange?: string): string {
  return useClock(60_000, (now) => exchangeDateOf(now, exchange))
}
